import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonComponent } from "@components/button/button.component";
import { SearchComponent } from "@components/search/search.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import _ from "lodash";
import { switchMap, take, tap } from "rxjs";
import { TypeAheadDTO } from "~app/common/models/common/TypeAheadDTO";
import { EntityDTO } from "~app/common/models/entity-models";
import { Atgard, SkogsvardEntity } from "../../models/atgard-models";
import { VirkeskopKontraktstyp } from "../../models/kontrakt-models";
import { CreateSkogsvard, SkogsvardSelectAtgardItem } from "../../models/skogsvard/createskogsvard-models";
import { AtgardKedjaMediatorService } from "../../services/atgardkedja-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { SkogsvardService } from "../../services/skogsvard.service";
import { VKAtgardHelper } from "../../utils/vkatgard-helper";
import { SkogsvardCreateRowComponent } from "../skogsvard-create-dialog/skogsvard-create-row/skogsvard-create-row.component";
import { SkogsvardSelectAtgardComponent } from "../skogsvard-create-dialog/skogsvard-select-atgard/skogsvard-select-atgard.component";

@Component({
    selector: "rask-skogsvard-create-och-sok-dialog",
    styleUrls: ["./skogsvard-create-och-sok-dialog.component.scss"],
    templateUrl: "./skogsvard-create-och-sok-dialog.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SkogsvardCreateRowComponent,
        ActionButtonComponent,
        SearchComponent,
        SkogsvardSelectAtgardComponent,
        ButtonComponent
    ]
})
export class SkogsvardCreateOchSokDialogComponent implements OnInit {
    modal = inject(NgbActiveModal);

    protected atgarderPreload: EntityDTO[];
    protected initialKontraktAtgarder: Atgard[];
    protected isSkogsvardKontrakt: boolean;
    protected kontraktAtgarder: Atgard[];
    protected kontraktTyp: string;
    protected listOfAtgarder: SkogsvardSelectAtgardItem[];
    protected preSelectedAtgard?: string;
    protected preSelectedKontraktGuid: string;
    protected readonly = false;
    protected selectedKontrakt: TypeAheadDTO;
    protected skogsvard: CreateSkogsvard;
    protected skogsvardGrupper: string[] = [];
    private readonly atgardKedjaMediatorService = inject(AtgardKedjaMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly skogsvardService = inject(SkogsvardService);
    init(atgarder: Atgard[], kontraktTyp: string, preSelectedAtgard: string, kontraktGuid: string, atgarderPreload: EntityDTO[]) {
        this.initialKontraktAtgarder = atgarder;
        this.kontraktTyp = kontraktTyp;
        this.isSkogsvardKontrakt = this.kontraktTyp === VirkeskopKontraktstyp.Skogsvard || this.kontraktTyp === VirkeskopKontraktstyp.SkogsvardFPV;
        this.preSelectedAtgard = preSelectedAtgard;
        this.preSelectedKontraktGuid = kontraktGuid;
        this.atgarderPreload = atgarderPreload;

        this.setDefaultKontraktAtgardList(kontraktGuid);
    }

    ngOnInit(): void {
        this.skogsvard = new CreateSkogsvard();

        this.kontraktMediatorService.kontrakt$.pipe(
            switchMap(kontrakt => this.skogsvardService.getSkogsvardGrupper(kontrakt.isSkogsvardFPVKontrakt)),
            tap(grupper => this.skogsvardGrupper = grupper),
            take(1)
        ).subscribe();
    }

    protected addSkogsvardRow() {
        this.skogsvard.addRow();
    }

    protected canSave() {
        const anyMetodOrGroupMissing = this.skogsvard.skogsvardRowTypes
            .some(row => !(row.skogsvardGrupp && row.skogsvardTyp));

        return !anyMetodOrGroupMissing;
    }

    protected close() {
        this.modal.dismiss();
    }

    protected delete(index: number) {
        this.skogsvard.skogsvardRowTypes.splice(index, 1);
    }

    protected save() {
        this.modal.close(this.skogsvard);
    }

    protected searchForKontraktForSkogsvardsAtgarderTypeAhead = (input: string) => this.skogsvardService.searchForKontraktForSkogsvardsAtgarderTypeAhead(input, this.kontraktTyp);

    protected selectedKontraktChanged($event) {
        this.selectedKontrakt = $event ? $event : null;

        if (_.isNil(this.selectedKontrakt)) {
            this.listOfAtgarder = [];
            this.setDefaultKontraktAtgardList(this.preSelectedKontraktGuid);
            return;
        }

        this.skogsvardService.getAllAtgarderForKontrakt(this.selectedKontrakt.id)
            .subscribe({
                next: (atgarder) => {
                    const filterAtgarder = atgarder.filter(atg => atg.nettoareal > 0 && atg.geometri);

                    const kontraktAtgarder = filterAtgarder.map((atg, index) =>
                        new SkogsvardSelectAtgardItem(
                            atg.kontraktGuid,
                            atg.atgardId,
                            atg.atgardGuid,
                            VKAtgardHelper.isAtgardsGruppSkogsvard(atg.atgardsGrupp) ? atg.atgardsTyp : atg.avverkningsGrupp,
                            `${atg.beteckning} - ${atg.nettoareal} ha`,
                            index,
                            VKAtgardHelper.isAtgardsGruppSkogsvard(atg.atgardsGrupp)
                        )
                    );
                    this.listOfAtgarder = kontraktAtgarder;
                },
                error: () => {
                    this.listOfAtgarder = [];
                }
            });
    }

    protected skogsvardAtgardChanged(item: SkogsvardSelectAtgardItem) {
        this.skogsvard.atgardCopyId = item?.atgardId;
        this.skogsvard.atgardCopyGuid = item?.value;
        this.skogsvard.kontraktCopyGuid = item?.kontraktGuid;
    }

    protected shouldCopyContractGeometriesChanged(value: boolean): void {
        this.skogsvard.copyContractGeometries = value;
    }

    protected shouldShowCopyContractGeometries(): boolean {
        return this.kontraktMediatorService?.atgarderPreload?.length === 0;
    }
    private setDefaultKontraktAtgardList(kontraktGuid: string) {
        const atgarderSorted = _.orderBy(this.initialKontraktAtgarder.filter(x => !x.isSkogsvard), "id");
        const listOfAtgarder: SkogsvardSelectAtgardItem[] = atgarderSorted.map((atg, index) => new SkogsvardSelectAtgardItem(
            kontraktGuid,
            atg.id,
            atg.guid,
            atg.avverkningsTyp?.grupp,
            `${atg.beteckning} - ${atg.nettoareal} ha`,
            index,
            false
        ));

        const skogsvardLista = this.atgarderPreload.filter(x => VKAtgardHelper.isAtgardEntitySkogsvard(x)) as SkogsvardEntity[];
        const skogsvardListSorted = this.atgardKedjaMediatorService.getSorteradAtgardslista(skogsvardLista);
        let indexCount = 0;
        skogsvardListSorted.forEach((entity) => {
            const atg = this.initialKontraktAtgarder.find(x => x.guid === entity.guid);

            if (atg?.geometri && atg?.nettoareal) {
                const selectionItem = new SkogsvardSelectAtgardItem(
                    kontraktGuid,
                    atg.id,
                    atg.guid,
                    atg.atgardstyp,
                    `${atg.beteckning} - ${atg.nettoareal} ha`,
                    indexCount,
                    true
                );
                listOfAtgarder.push(selectionItem);
            }
            indexCount++;
        });

        this.listOfAtgarder = listOfAtgarder;
    }
}
